/**
 * Clinician-facing model catalogue for platform chat.
 *
 * `bedrockId: null` means "omit model_id and let the server use its default"
 * (CHAT_TOOL_CALLING_MODEL_ID, Nova Pro). Keep ids in sync with
 * `PLATFORM_CHAT_ALLOWED_MODELS` in the API.
 */
export const PLATFORM_CHAT_MODEL_STORAGE_KEY = 'platform-chat-model-id';

export type PlatformChatModel = {
  id: string;
  label: string;
  description: string;
  bedrockId: string | null;
};

export const PLATFORM_CHAT_MODELS: readonly PlatformChatModel[] = [
  {
    id: 'default',
    label: 'pmx-clinician-v3',
    description: 'Default · Nova Pro',
    bedrockId: null,
  },
  {
    id: 'fast',
    label: 'Fast',
    description: 'Nova Lite · quicker, lighter',
    bedrockId: 'us.amazon.nova-lite-v1:0',
  },
  {
    id: 'clinical',
    label: 'Clinical',
    description: 'Claude Sonnet 4.6 · stronger clinical reasoning',
    bedrockId: 'us.anthropic.claude-sonnet-4-6',
  },
  {
    id: 'reasoning',
    label: 'Reasoning',
    description: 'Claude Opus 4.6 · deepest, slowest',
    bedrockId: 'us.anthropic.claude-opus-4-6-v1',
  },
];

const DEFAULT_MODEL = PLATFORM_CHAT_MODELS[0];

export const resolvePlatformChatModel = (id?: string | null): PlatformChatModel =>
  PLATFORM_CHAT_MODELS.find((model) => model.id === id) ?? DEFAULT_MODEL;

export const loadPlatformChatModelId = (): string => {
  try {
    return resolvePlatformChatModel(localStorage.getItem(PLATFORM_CHAT_MODEL_STORAGE_KEY)).id;
  } catch {
    return DEFAULT_MODEL.id;
  }
};

export const savePlatformChatModelId = (id: string): void => {
  try {
    localStorage.setItem(PLATFORM_CHAT_MODEL_STORAGE_KEY, resolvePlatformChatModel(id).id);
  } catch {
    /* quota exceeded — selection still applies for this session via callers */
  }
};

/** Bedrock id to send, or null to let the server pick. Read at send time. */
export const getSelectedPlatformChatBedrockId = (): string | null =>
  resolvePlatformChatModel(loadPlatformChatModelId()).bedrockId;
